/**
 * AttentionInboxPage — the operator's cross-employee attention inbox
 * (DIGEMP-ATTENTION-INBOX-FRONTEND-066).
 *
 * Answers one question: "what, across all Digital Employees, is waiting on a human right now —
 * and what is the next action for each item?" Every group here is a projection of an existing
 * authoritative AgentRun status (or a derivation from its recorded timestamps) read through the
 * existing agents_view-gated endpoints. Nothing on this page performs an action: each item links
 * to the run detail / ticket / conversation screen where the existing permission gates
 * (agents_approve for approve/reject, etc.) already apply, so no gate is re-implemented, bypassed
 * or weakened here.
 *
 * Truthfulness rules:
 *   - A failed group fetch renders "unknown", never "0 items".
 *   - A missing timestamp reads "משך ההמתנה אינו ידוע", never a guessed duration.
 *   - Evidence lines are bounded free text from the run record; a run with no recorded analysis
 *     or failure summary says so, and failures with no recorded evidence are cross-listed in the
 *     stale-evidence group.
 *   - An evidence line derived from `run.analysis` carries that analysis's provenance first: a
 *     `degraded` analysis (the pilot default's deterministic mock, or a model reply that failed to
 *     parse) is labelled before the confidence figure and proposed action it qualifies, so the row
 *     never presents the fallback adapter's scaffolding as the employee's own read of the ticket.
 *   - Raw run context is never rendered: only the whitelisted `connection_state` scalar (mapped
 *     through an allow-list) and `conversation_id` (used only to build a link) are read.
 */
import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import { agentRunsApi, agentsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import CriticalStateBadge from "../components/designSystem/CriticalStateBadge";
import RoleSurface from "../components/designSystem/RoleSurface";
import EmployeeAvatar from "../components/EmployeeAvatar";
import { degradedAnalysisLabel } from "../components/degradedAnalysisVocabulary";
import { ACTION_TYPE_LABEL } from "../components/actionTypeVocabulary";

// ─── Constants ───────────────────────────────────────────────────────────────

// Each group fetches at most this many items; when the cap is hit the section says so instead of
// silently pretending the list is complete.
const GROUP_LIMIT = 50;

// A wait is "stale evidence" once the run's last recorded change is older than this: the state
// the operator is looking at is a day old, so it should be re-verified before acting on it.
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

// Presented in the order a wait blocks progress: an action approval and a technician review
// block a run outright; customer-side waits block it on someone outside the team; failures and
// paused runs need an explicit human decision. The stale-evidence group is a cross-cut lens over
// all of the above and is rendered last.
const WAIT_GROUPS = [
  {
    key: "approval",
    status: "awaiting_approval",
    label: "ממתין לאישור",
    icon: "🛡️",
    hint: "פעולה מוצעת ממתינה לאישור אנושי לפני ביצוע.",
    actionLabel: "אשר או דחה",
  },
  {
    key: "review",
    status: "awaiting_technician_review",
    label: "ממתין לסקירת טכנאי",
    icon: "🔎",
    hint: "הניתוח הושלם וממתין להחלטת טכנאי: אישור, דחייה או עריכה.",
    actionLabel: "סקור ואשר",
  },
  {
    key: "user_answer",
    status: "waiting_for_customer_answer",
    label: "ממתין לתשובת לקוח",
    icon: "💬",
    hint: "נשאלה שאלה והריצה ממתינה לתשובת הלקוח.",
    actionLabel: "פתח ריצה",
    fullListStatus: null, // the runs dashboard has no filter option for this status
  },
  {
    key: "connection",
    status: "waiting_for_customer_connection",
    label: "ממתין לחיבור לקוח",
    icon: "🔌",
    hint: "נדרש חיבור מרחוק אל הלקוח כדי להמשיך.",
    actionLabel: "פתח ריצה",
  },
  {
    key: "failure",
    status: "failed",
    label: "ריצה נכשלה",
    icon: "⚠️",
    hint: "הריצה נכשלה וממתינה להחלטה: ניסיון חוזר או טיפול ידני.",
    actionLabel: "טפל בכשל",
  },
  {
    key: "escalation",
    status: "paused",
    label: "הסלמה — ריצה מושהית",
    icon: "⏸️",
    hint: "הריצה הושהתה וממתינה להחלטה אנושית: חידוש או סגירה.",
    actionLabel: "המשך או סגור",
  },
];

const STALE_KEY = "stale_evidence";

// Only these recorded connection states are shown; any other value stays unrendered (the run
// context is free-form and is never surfaced raw).
const CONNECTION_STATE_HE = {
  requested: "התבקש חיבור מהלקוח",
  session_ready: "חיבור מרחוק הוכן וממתין ללקוח",
};

const FAILURE_SUMMARY_MAX = 140;

// ─── Pure derivations (exported for focused tests) ───────────────────────────

export function lastKnownChangeAt(run) {
  return run?.updated_at || run?.created_at || null;
}

export function waitingMs(run, now) {
  const at = lastKnownChangeAt(run);
  if (!at) return null;
  const parsed = Date.parse(at);
  if (Number.isNaN(parsed)) return null;
  return Math.max(0, now - parsed);
}

export function formatWaitDuration(ms) {
  if (ms == null) return null;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "פחות מדקה";
  if (minutes < 60) return minutes === 1 ? "דקה" : `${minutes} דקות`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? "שעה" : `${hours} שעות`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "יום" : `${days} ימים`;
}

export function isStaleWait(run, now) {
  const ms = waitingMs(run, now);
  return ms != null && ms > STALE_AFTER_MS;
}

export function failureEvidenceMissing(run) {
  const summary = typeof run?.result_summary === "string" ? run.result_summary.trim() : "";
  return summary === "" && !run?.analysis;
}

/**
 * The stale-evidence group is a lens over the other groups, not a seventh status query:
 *   - a waiting item whose last recorded change is over 24h old, and
 *   - a failed run with no recorded evidence of why it failed.
 * Origin membership is kept so the row can say which group it came from.
 */
export function buildStaleEntries(groupResults, now) {
  const entries = [];
  for (const group of groupResults) {
    if (group.error || !group.items) continue;
    for (const run of group.items) {
      if (group.key === "failure") {
        if (failureEvidenceMissing(run)) {
          entries.push({ run, originKey: "failure", reason: "missing_evidence" });
        }
      } else if (isStaleWait(run, now)) {
        entries.push({ run, originKey: group.key, reason: "wait_over_24h" });
      }
    }
  }
  return entries;
}

function analysisEvidence(run) {
  const analysis = run?.analysis;
  if (!analysis) return { text: "לא תועד ניתוח לריצה זו", unknown: true };
  const parts = [];
  if (Number.isFinite(analysis.confidence)) parts.push(`ביטחון ${analysis.confidence}%`);
  const actionHe = ACTION_TYPE_LABEL[analysis.proposed_action?.action_type];
  if (actionHe) parts.push(`פעולה מוצעת: ${actionHe}`);
  // The figure and the proposed action are the employee's own only if a model produced them. On the
  // pilot default (no ANALYSIS_PROVIDER) they are the fallback adapter's: a confidence derived from
  // ticket richness and a rule-matched action. Every other surface that renders this run labels that
  // provenance, so this row carries it too, ahead of the content it qualifies.
  const degraded = degradedAnalysisLabel(analysis.degraded);
  if (parts.length === 0) {
    return { text: "הניתוח תועד ללא פרטי ביטחון או פעולה מוצעת", unknown: true, degraded };
  }
  return { text: parts.join(" · "), unknown: false, degraded };
}

function failureEvidence(run) {
  const summary = typeof run?.result_summary === "string" ? run.result_summary.trim() : "";
  if (!summary) return { text: "לא תועד מידע על סיבת הכשל", unknown: true };
  return {
    text: summary.length > FAILURE_SUMMARY_MAX ? `${summary.slice(0, FAILURE_SUMMARY_MAX)}…` : summary,
    unknown: false,
  };
}

function connectionEvidence(run) {
  const state = run?.context?.connection_state;
  const known = CONNECTION_STATE_HE[state];
  if (known) return { text: known, unknown: false };
  return { text: "פרטי החיבור לא תועדו", unknown: true };
}

function escalationEvidence(run) {
  // A paused run with no recorded analysis is the backend's "dead paused" shape: resume is a
  // no-op for it (AgentRun.analysis stays null, retry accepts only failed). Saying so beats an
  // operator pressing "resume" and seeing nothing happen.
  if (!run?.analysis) {
    return { text: "לא תועד ניתוח — ייתכן שהריצה נעצרה לפני שהחלה, וחידוש עשוי שלא לקדם אותה.", unknown: true };
  }
  return null;
}

function evidenceFor(groupKey, run) {
  if (groupKey === "failure") return failureEvidence(run);
  if (groupKey === "connection") return connectionEvidence(run);
  if (groupKey === "escalation") return escalationEvidence(run);
  if (groupKey === "approval" || groupKey === "review") return analysisEvidence(run);
  return null;
}

function conversationIdOf(run) {
  const id = run?.context?.conversation_id;
  return typeof id === "string" && id.length > 0 && id.length <= 64 ? id : null;
}

function sortItems(groupKey, runs) {
  const copy = [...runs];
  const at = (run) => Date.parse(lastKnownChangeAt(run) || "") || 0;
  // Longest wait first for waiting groups; freshest failure first for the failure group.
  copy.sort((a, b) => (groupKey === "failure" ? at(b) - at(a) : at(a) - at(b)));
  return copy;
}

// ─── Building blocks ─────────────────────────────────────────────────────────

const GROUP_TITLE_BADGE = {
  approval: "bg-amber-100 text-amber-700",
  review: "bg-amber-100 text-amber-700",
  user_answer: "bg-blue-100 text-blue-700",
  connection: "bg-blue-100 text-blue-700",
  failure: "bg-red-100 text-red-700",
  escalation: "bg-gray-200 text-gray-600",
  [STALE_KEY]: "bg-purple-100 text-purple-700",
};

export function ticketLabel(run) {
  if (run?.ticket_number != null) {
    return run.ticket_title ? `קריאה #${run.ticket_number} — ${run.ticket_title}` : `קריאה #${run.ticket_number}`;
  }
  return "ללא קריאה משויכת";
}

function ItemRow({ run, group, agent, now, originKey, staleReason }) {
  const ticket = ticketLabel(run);
  const evidence = evidenceFor(group.key, run);
  const waitMs = waitingMs(run, now);
  const waitText = formatWaitDuration(waitMs);
  const conversationId = conversationIdOf(run);
  const agentName = agent?.name || run.agent_name || "עובד לא מזוהה";
  const originGroup = originKey ? WAIT_GROUPS.find((g) => g.key === originKey) : null;
  const actionLabel = group.actionLabel;

  return (
    <article className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 flex items-start justify-between gap-3">
      <div className="flex items-start gap-3 min-w-0">
        <EmployeeAvatar
          name={agentName}
          avatarKey={agent?.avatar_key}
          avatarColor={agent?.avatar_color}
          size="sm"
        />
        <div className="min-w-0 space-y-0.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-800">{agentName}</span>
            {originGroup && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${GROUP_TITLE_BADGE[originGroup.key]}`}>
                מקור: {originGroup.label}
              </span>
            )}
          </div>
          <div className="text-sm text-gray-700 truncate" title={ticket}>
            {run?.ticket_id ? (
              <Link to={`/tickets/${run.ticket_id}`} className="hover:underline">
                {ticket}
              </Link>
            ) : (
              ticket
            )}
          </div>
          {evidence?.degraded && (
            <div
              data-testid={`attention-inbox-degraded-${run.id}`}
              className="inline-block text-[10px] font-medium leading-none bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5"
            >
              {evidence.degraded}
            </div>
          )}
          {evidence && (
            <div
              className={`text-xs ${evidence.unknown ? "text-gray-400 italic" : "text-gray-500"}`}
              title={evidence.text}
            >
              {evidence.text}
            </div>
          )}
          {staleReason === "missing_evidence" && (
            <div className="text-xs text-purple-700">
              אין תיעוד של סיבת הכשל — יש לבדוק את הריצה לפני החלטה.
            </div>
          )}
          <div className="text-xs text-gray-500">
            {waitText != null ? `ממתין כבר ${waitText}` : "משך ההמתנה אינו ידוע"}
          </div>
        </div>
      </div>

      <div className="flex flex-col items-end gap-1 shrink-0">
        <Link
          to={`/agent-runs/${run.id}`}
          aria-label={`${actionLabel} — ${ticket}`}
          className="text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 whitespace-nowrap"
        >
          {actionLabel}
        </Link>
        {conversationId && (
          <Link
            to={`/conversations/${conversationId}`}
            aria-label={`פתח שיחה — ${ticket}`}
            className="text-xs text-blue-700 hover:underline"
          >
            לשיחה
          </Link>
        )}
      </div>
    </article>
  );
}

function GroupSection({ group, state, agentsById, now, staleEntries, staleLoading, fullListHref }) {
  const isStaleGroup = group.key === STALE_KEY;
  const count = isStaleGroup ? (staleLoading ? null : staleEntries.length) : state?.error ? null : (state?.items?.length ?? 0);
  const loading = isStaleGroup ? staleLoading : state?.loading;
  const errored = isStaleGroup ? false : state?.error;
  return (
    <section aria-label={group.label} className="space-y-2">
      <div className="flex items-center gap-2">
        <span aria-hidden="true">{group.icon}</span>
        <h2 className="text-base font-semibold text-gray-800">{group.label}</h2>
        {count != null && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${GROUP_TITLE_BADGE[group.key]}`}>{count}</span>
        )}
      </div>
      <p className="text-xs text-gray-500">{group.hint}</p>

      {loading && <p className="text-sm text-gray-400">טוען…</p>}

      {errored && !loading && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          לא ניתן לטעון קבוצה זו — ייתכן שיש כאן פריטים שאינם מוצגים, והמצב אינו ידוע.
        </p>
      )}

      {!errored && !loading && count === 0 && !isStaleGroup && (
        <p className="text-sm text-gray-400">אין פריטים בקבוצה זו.</p>
      )}

      {!errored && !loading && isStaleGroup && staleEntries.length === 0 && (
        <p className="text-sm text-gray-400">לא נמצאו פריטים עם עדות מיושנת.</p>
      )}

      <div className="space-y-2">
        {(isStaleGroup ? staleEntries : state?.items ?? []).map((entry) => {
          const run = isStaleGroup ? entry.run : entry;
          const originKey = isStaleGroup ? entry.originKey : null;
          const renderGroup = isStaleGroup ? WAIT_GROUPS.find((g) => g.key === entry.originKey) : group;
          return (
            <ItemRow
              key={run.id}
              run={run}
              group={renderGroup}
              agent={agentsById.get(run.agent_id)}
              now={now}
              originKey={originKey}
              staleReason={isStaleGroup ? entry.reason : null}
            />
          );
        })}
      </div>

      {!errored && !isStaleGroup && count === GROUP_LIMIT && (
        <p className="text-xs text-gray-500">
          מוצגים {GROUP_LIMIT} הפריטים הראשונים — ייתכנו פריטים נוספים.{" "}
          {fullListHref && (
            <Link to={fullListHref} className="text-blue-700 hover:underline">
              לרשימה המלאה
            </Link>
          )}
        </p>
      )}
    </section>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function AttentionInboxPage() {
  const { can, loading: permsLoading } = useAgentPermissions();
  const canView = can("agents_view");
  const canManage = can("agents_manage");

  const [groupFilter, setGroupFilter] = useState("");
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const groupQueries = useQueries({
    queries: WAIT_GROUPS.map((group) => ({
      queryKey: ["attention-inbox", group.status],
      queryFn: () => agentRunsApi.listAll({ status: group.status, limit: GROUP_LIMIT }),
      enabled: canView,
      staleTime: 15_000,
      refetchInterval: 20_000,
    })),
  });

  const agentsQuery = useQuery({
    queryKey: ["attention-inbox-agents"],
    queryFn: () => agentsApi.list(),
    enabled: canView,
    staleTime: 30_000,
  });

  // Crisis mode is readable only with agents_manage; without it the page simply makes no claim
  // about crisis state (and never attempts the managed-only call).
  const crisisQuery = useQuery({
    queryKey: ["attention-inbox-crisis"],
    queryFn: () => agentsApi.getCrisisMode(),
    enabled: canView && canManage,
    staleTime: 30_000,
  });

  const agentsById = useMemo(() => {
    const map = new Map();
    for (const agent of agentsQuery.data ?? []) map.set(agent.id, agent);
    return map;
  }, [agentsQuery.data]);

  // Plain per-render derivation: the group list is static and bounded (6 statuses × 50 rows).
  const groupResults = WAIT_GROUPS.map((group, i) => {
    const q = groupQueries[i];
    return {
      key: group.key,
      items: q.data ? sortItems(group.key, q.data) : null,
      error: q.isError && !q.data,
      loading: q.isLoading,
    };
  });

  const staleEntries = buildStaleEntries(groupResults, now);

  const counts = new Map();
  for (const result of groupResults) counts.set(result.key, result.error ? null : (result.items?.length ?? 0));
  counts.set(STALE_KEY, staleEntries.length);

  let totalKnown = 0;
  for (const group of WAIT_GROUPS) {
    const c = counts.get(group.key);
    if (c != null) totalKnown += c;
  }

  const anyGroupError = groupResults.some((r) => r.error);
  const anyGroupLoading = groupResults.some((r) => r.loading);
  const newestUpdate = Math.max(0, ...groupQueries.map((q) => q.dataUpdatedAt || 0));

  if (permsLoading) {
    return <div className="p-6 text-gray-500">טוען הרשאות…</div>;
  }

  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות בתיבת תשומת הלב התפעולית</div>;
  }

  const staleGroup = {
    key: STALE_KEY,
    label: "עדות מיושנת",
    icon: "🕰️",
    hint:
      "המתנות שרשומתן האחרונה מלפני יותר מ-24 שעות, וריצות כשל ללא תיעוד סיבה. " +
      "פריטים אלה מופיעים גם בקבוצת המקור שלהם — יש לאמת את המצב לפני פעולה.",
    actionLabel: "אמת וטפל",
  };

  const visibleGroups = groupFilter
    ? groupFilter === STALE_KEY
      ? [staleGroup]
      : WAIT_GROUPS.filter((g) => g.key === groupFilter)
    : [...WAIT_GROUPS, staleGroup];

  const chipGroups = [...WAIT_GROUPS, staleGroup];

  return (
    <RoleSurface
      role="command_center"
      dir="rtl"
      title="תיבת תשומת לב תפעולית"
      description="כל ההמתנות, הכשלים וההסלמות של העובדים הדיגיטליים במקום אחד — עם הקישור לפעולה הבאה בכל פריט."
      actions={
        <button
          type="button"
          onClick={() => groupQueries.forEach((q) => q.refetch())}
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 hover:bg-gray-50"
        >
          רענן
        </button>
      }
      meta={
        newestUpdate > 0
          ? `נטען לאחרונה: ${new Date(newestUpdate).toLocaleTimeString("he-IL")}${
              anyGroupError ? " · חלק מהקבוצות לא נטענו" : ""
            }`
          : null
      }
      testId="attention-inbox-role-surface"
    >

      {canManage && (
        <>
          {crisisQuery.isError && !crisisQuery.data && (
            <div className="rounded-lg border border-gray-300 bg-gray-50 text-gray-700 text-sm p-3 flex items-start gap-2 flex-wrap">
              <CriticalStateBadge state="unknown" testId="attention-crisis-unknown" />
              <span>לא ניתן לטעון את מצב החירום — המצב אינו ידוע.</span>
            </div>
          )}
          {crisisQuery.data?.active === true && (
            <div className="rounded-lg border border-red-300 bg-red-50 text-red-800 text-sm p-3 flex items-start gap-2 flex-wrap">
              <CriticalStateBadge state="blocked" testId="attention-crisis-blocked" />
              <span>
                מצב חירום פעיל — פעולות הכתיבה של העובדים מושהות. ייתכן שאישורים לא יבוצעו עד ליציאה ממצב חירום.
              </span>
            </div>
          )}
        </>
      )}

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="סינון לפי קבוצה">
        <button
          type="button"
          aria-pressed={groupFilter === ""}
          onClick={() => setGroupFilter("")}
          className={`text-xs rounded-full px-3 py-1.5 border ${
            groupFilter === "" ? "bg-gray-800 text-white border-gray-800" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
          }`}
        >
          הצג הכל ({totalKnown})
        </button>
        {chipGroups.map((group) => {
          const count = counts.get(group.key);
          return (
            <button
              key={group.key}
              type="button"
              aria-pressed={groupFilter === group.key}
              onClick={() => setGroupFilter(group.key)}
              className={`text-xs rounded-full px-3 py-1.5 border ${
                groupFilter === group.key
                  ? "bg-gray-800 text-white border-gray-800"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {group.label} {count == null ? "?" : `(${count})`}
            </button>
          );
        })}
      </div>

      {!anyGroupLoading && !anyGroupError && totalKnown === 0 && staleEntries.length === 0 && (
        <div className="rounded-xl border border-green-200 bg-green-50 text-green-800 p-4 text-sm">
          אין פריטים הדורשים תשומת לב כרגע — כל התורים מטופלים.
        </div>
      )}

      {anyGroupError && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm p-3">
          חלק מהקבוצות לא נטענו — מספר הפריטים הכולל אינו ודאי.
        </div>
      )}

      {visibleGroups.map((group) => (
        <GroupSection
          key={group.key}
          group={group}
          state={groupResults.find((r) => r.key === group.key)}
          agentsById={agentsById}
          now={now}
          staleEntries={staleEntries}
          staleLoading={anyGroupLoading}
          fullListHref={
            group.key === STALE_KEY || group.fullListStatus === null
              ? undefined
              : `/agent-runs?status=${group.status}`
          }
        />
      ))}
    </RoleSurface>
  );
}
